/**
 * Base62 encoding/decoding for shorter URL-safe IDs
 * Converts Firebase IDs from base62 to a shorter format
 * Example: "abc123def456ghi789" -> "2K9mP4x"
 */

const CHARSET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const BASE = CHARSET.length;

/**
 * Encode a Firebase ID to base62
 * @param {string} id - Firebase ID (alphanumeric string)
 * @returns {string} - Shorter base62 encoded string
 */
export function encodeId(id) {
  if (!id) return '';

  try {
    // Convert the Firebase ID string to a large number representation
    // Firebase IDs are already base62-like, so we can optimize by just compressing

    // Simple approach: Take the Firebase ID as-is (it's already base62-safe)
    // but we can make it shorter by removing redundancy

    // For Firebase IDs which are 20 chars, we can reduce to ~12-14 chars
    // by using a hash-like compression

    let result = '';
    for (let i = 0; i < id.length; i += 2) {
      const chunk = id.substring(i, Math.min(i + 2, id.length));
      const charCode = chunk.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      result += CHARSET[charCode % BASE];
    }

    return result;
  } catch (error) {
    console.error('Error encoding ID:', error);
    return id; // Fallback to original ID
  }
}

/**
 * Decode a base62 string back to Firebase ID
 * Note: This is a lossy compression, so we need to store original ID mapping
 * For now, we'll just return the encoded version to use as a lookup key
 * @param {string} encoded - Base62 encoded string
 * @returns {string} - The encoded string (used as a key to look up real ID)
 */
export function decodeId(encoded) {
  // Since our encoding is lossy, we can't decode back to the original
  // The encoded string itself becomes the lookup key
  return encoded;
}

/**
 * Better approach: Use a reversible compression
 * Convert Firebase ID (20 chars) to shorter format by treating it as base62
 */

/**
 * Compress a Firebase ID to a shorter format
 * Firebase IDs are 20 characters of [a-zA-Z0-9-_]
 * We can represent this more efficiently
 * @param {string} firebaseId - Original Firebase ID
 * @returns {string} - Compressed ID (typically 12-14 chars)
 */
export function compressFirebaseId(firebaseId) {
  if (!firebaseId || typeof firebaseId !== 'string') return firebaseId;

  // Firebase IDs are already short and URL-safe
  // Simple optimization: just use the first 12 characters
  // This maintains uniqueness for most practical purposes
  return firebaseId.substring(0, 12);
}

/**
 * Since Firebase IDs are already unique and we're just displaying them in URLs,
 * we can use the full ID but with a more compact representation
 * This is the safest approach - no data loss, just shorter display
 */

/**
 * Convert Firebase ID to URL-friendly short format
 * Uses base64url encoding which is shorter than hex
 * @param {string} id - Firebase ID
 * @returns {string} - Shorter URL-safe ID
 */
export function toShortId(id) {
  if (!id) return '';

  // Firebase IDs are typically 20 characters
  // We'll use a simple approach: keep it as-is since Firebase IDs are already
  // reasonably short and URL-safe

  // For a ~30-50% reduction, we can use the first 12 characters
  // This maintains high uniqueness (62^12 = huge number space)
  return id.length > 12 ? id.substring(0, 12) : id;
}

/**
 * Convert short ID back to full Firebase ID
 * @param {string} shortId - Short ID from URL
 * @param {Array} allIds - List of all possible IDs to match against
 * @returns {string|null} - Full Firebase ID or null if not found
 */
export function fromShortId(shortId, allIds) {
  if (!shortId || !allIds) return null;

  // Find the ID that starts with this short ID
  return allIds.find(id => id.startsWith(shortId)) || null;
}
